import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { AcaoIndicativoNascimento, determinarAcaoIndicativoNascimento, incluirIntegranteIndicativoNascimento, obterIntegrantesIndicativoNascimento } from '../../api/indicativoNascimentoAPI';
import auth from '../../auth/Auth';

export interface IntegranteIndicativoNascimento {
  cpf: string;
  nome: string;
  dataNascimento: string;
  sexo?: string;
  filiacao1?: string;
  filiacao2?: string;
  matricula?: string;
  numeroCartorioCertidao?: string;
  nomeCartorioPessoa?: string;
  nuIbgeMunicipioCertidao?: number;
  dataLavratura?: string;
}

export interface FamiliaIndicativoNascimento {
  nuFamiliar: string;
  integrantesIndicativoNascimento: IntegranteIndicativoNascimento[];
}

export type EtapaIndicativo =
  | 'inicio'
  | 'confirmacao'
  | 'perguntas'
  | 'resumo'
  | 'compareca-ao-cras'
  | 'compareca-ao-cras-inclusao'
  | 'conclusao'
  | 'erro'
  | 'erro-horario'
  | 'erro-servico';

const SESSION_KEY = (cpf: string) => `cadunico/indicativo-nascimento-consultado/${cpf}`;

export const jaConsultadoIndicativoNascimento = (cpf: string) =>
  sessionStorage.getItem(SESSION_KEY(cpf)) === 'true';

export const marcarConsultadoIndicativoNascimento = (cpf: string) =>
  sessionStorage.setItem(SESSION_KEY(cpf), 'true');

interface IndicativoNascimentoState {
  mostrarModal: boolean;
  etapa: EtapaIndicativo;
  etapaPerguntaAtual: number;
  respostas: Record<string, string>;
  familiaIndicativoNascimento: FamiliaIndicativoNascimento | null;
  indiceAtualIntegrante: number;
}

const initialState: IndicativoNascimentoState = {
  mostrarModal: true,
  etapa: 'inicio',
  etapaPerguntaAtual: 0,
  respostas: {},
  familiaIndicativoNascimento: null,
  indiceAtualIntegrante: 0,
};

const PARENTESCO_MAP: Record<string, number> = {
  filho: 3, enteado: 4, neto_bisneto: 5, irmao: 8, outro_parente: 10, nao_parente: 11,
};
const COR_RACA_MAP: Record<string, number> = {
  branca: 1, preta: 2, amarela: 3, parda: 4, indigena: 5,
};
const SEXO_MAP: Record<string, string> = {
  feminino: 'F', masculino: 'M',
};

export const salvarIntegranteIndicativoNascimento = createAsyncThunk(
  'indicativoNascimento/salvarIntegranteIndicativoNascimento',
  async ({ numeroFamiliar, integrante, respostas }: {
    numeroFamiliar: string;
    integrante: IntegranteIndicativoNascimento;
    respostas: Record<string, string>;
  }) => {
    let filiacao2 = '';
    let naoSabeNomeCompletoPai = false;
    const filiacaoRaw = respostas['filiacao'];
    if (filiacaoRaw) {
      try {
        const parsed = JSON.parse(filiacaoRaw);
        if (parsed.filiacao2 === 'nao_sei') {
          naoSabeNomeCompletoPai = true;
        } else {
          filiacao2 = parsed.filiacao2 || '';
        }
      } catch { /* Tratamento para caso ocorra algum erro ao tentar incluir novo integrante. */ }
    }
    const sexo = integrante.sexo
      ? (SEXO_MAP[integrante.sexo] ?? integrante.sexo)
      : (SEXO_MAP[respostas['sexo']] ?? respostas['sexo'] ?? '');
    const dto = {
      cpf: integrante.cpf,
      nome: integrante.nome,
      dataNascimento: integrante.dataNascimento,
      sexo,
      filiacao1: integrante.filiacao1 ?? '',
      filiacao2,
      relacaoParentesco: PARENTESCO_MAP[respostas['parentesco']] ?? null,
      corRaca: COR_RACA_MAP[respostas['cor_raca']] ?? null,
      naoSabeNomeCompletoPai: Boolean(naoSabeNomeCompletoPai),
      matricula: integrante.matricula ?? '',
      numeroCartorioCertidao: integrante.numeroCartorioCertidao ?? '',
      nomeCartorioPessoa: integrante.nomeCartorioPessoa ?? '',
      nuIbgeMunicipioCertidao: integrante.nuIbgeMunicipioCertidao ?? undefined,
      dataLavratura: integrante.dataLavratura ?? '',
    };
    return incluirIntegranteIndicativoNascimento(numeroFamiliar, dto);
  },
  { serializeError: (err: any) => ({ message: err.message, codigo: err.codigo }) }
);

export const registrarAcaoIndicativoNascimento = createAsyncThunk(
  'indicativoNascimento/registrarAcaoIndicativoNascimento',
  async ({ numeroFamiliar, cpfIntegrante, codigoAcao }: {
    numeroFamiliar: string;
    cpfIntegrante: string;
    codigoAcao: AcaoIndicativoNascimento;
  }) => {
    return determinarAcaoIndicativoNascimento(numeroFamiliar, {
      cpfUsuarioSolicitante: auth.getUser()!,
      cpfIndicativoNascimento: cpfIntegrante,
      codigoAcao,
    });
  }
);

export const getIntegrantesIndicativoNascimento = createAsyncThunk(
  'indicativoNascimento/getIntegrantesIndicativoNascimento',
  async (numeroFamiliar: string) => {
    return obterIntegrantesIndicativoNascimento(numeroFamiliar);
  }
);

export const indicativoNascimentoSlice = createSlice({
  name: 'indicativoNascimento',
  initialState,
  reducers: {
    setMostrarModalIndicativoNascimento: (state, action) => {
      state.mostrarModal = action.payload;
    },
    setEtapaIndicativo: (state, action) => {
      state.etapa = action.payload;
    },
    setEtapaPerguntaAtual: (state, action) => {
      state.etapaPerguntaAtual = action.payload;
    },
    setRespostaIndicativo: (state, action) => {
      state.respostas = { ...state.respostas, ...action.payload };
    },
    limparIndicativoNascimento: (state) => {
      state.etapa = 'inicio';
      state.etapaPerguntaAtual = 0;
      state.respostas = {};
      state.indiceAtualIntegrante = 0;
    },
    avancarParaProximoIntegrante: (state) => {
      state.etapa = 'inicio';
      state.etapaPerguntaAtual = 0;
      state.respostas = {};
      state.indiceAtualIntegrante += 1;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getIntegrantesIndicativoNascimento.fulfilled, (state, action) => {
      console.log('[IndicativoNascimento] Resultado da consulta:', action.payload);
      state.familiaIndicativoNascimento = action.payload;
    });
    builder.addCase(getIntegrantesIndicativoNascimento.rejected, (state, error) => {
      console.error('[IndicativoNascimento] Erro na consulta:', error);
    });
  },
});

export const {
  setMostrarModalIndicativoNascimento,
  setEtapaIndicativo,
  setEtapaPerguntaAtual,
  setRespostaIndicativo,
  limparIndicativoNascimento,
  avancarParaProximoIntegrante,
} = indicativoNascimentoSlice.actions;

export const selectMostrarModalIndicativoNascimento = (state: RootState) =>
  state.indicativoNascimento.mostrarModal;
export const selectEtapaIndicativo = (state: RootState) =>
  state.indicativoNascimento.etapa;
export const selectEtapaPerguntaAtual = (state: RootState) =>
  state.indicativoNascimento.etapaPerguntaAtual;
export const selectRespostasIndicativo = (state: RootState) =>
  state.indicativoNascimento.respostas;
export const selectFamiliaIndicativoNascimento = (state: RootState) =>
  state.indicativoNascimento.familiaIndicativoNascimento;
export const selectIndiceAtualIntegrante = (state: RootState) =>
  state.indicativoNascimento.indiceAtualIntegrante;
export const selectIntegranteIndicativoNascimento = (state: RootState) => {
  const lista = state.indicativoNascimento.familiaIndicativoNascimento?.integrantesIndicativoNascimento;
  const indice = state.indicativoNascimento.indiceAtualIntegrante;
  return lista?.[indice] ?? null;
};
export const selectTemProximoIntegrante = (state: RootState) => {
  const lista = state.indicativoNascimento.familiaIndicativoNascimento?.integrantesIndicativoNascimento;
  const indice = state.indicativoNascimento.indiceAtualIntegrante;
  return !!lista && indice + 1 < lista.length;
};

export default indicativoNascimentoSlice.reducer;
